/**
 * Access-token lifecycle.
 *
 * Access tokens are deliberately short-lived (15 minutes) so a stolen one is
 * only briefly useful. Keeping a signed-in user working across that boundary is
 * this module's job: it renews the token from the httpOnly refresh cookie
 * shortly before expiry, so nobody is bounced to the login screen mid-task.
 *
 * The session itself still ends after 12 hours of inactivity — the server drops
 * the session record, the refresh call fails, and the user signs in again. That
 * is the intended behaviour, not a bug to work around here.
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';

export const TOKEN_STORAGE_KEY = 'policyora_token';

/** Renew this long before the token expires, so a slow request never races expiry. */
const REFRESH_MARGIN_MS = 2 * 60 * 1000; // 2 minutes
/** Fallback cadence if the token carries no readable expiry. */
const DEFAULT_REFRESH_MS = 13 * 60 * 1000; // 13 minutes

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
/** In-flight refresh, shared so concurrent 401s trigger only one network call. */
let inFlight: Promise<string | null> | null = null;

export function getAccessToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setAccessToken(token: string): void {
  try {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    /* private browsing — the token simply won't persist across reloads */
  }
}

export function clearAccessToken(): void {
  try {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Milliseconds until the token expires, or null if it cannot be read. */
function millisUntilExpiry(token: string): number | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof claims.exp !== 'number') return null;
    return claims.exp * 1000 - Date.now();
  } catch {
    // A malformed token is not worth failing over — fall back to the fixed cadence.
    return null;
  }
}

/**
 * Exchange the refresh cookie for a new access token.
 *
 * Concurrent callers share one request: without this, several components each
 * hitting a 401 would fire simultaneous refreshes and race each other.
 * Returns null when the session is over and the caller should sign in again.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        // The refresh token is an httpOnly cookie, so it must be sent explicitly.
        credentials: 'include',
      });

      if (!response.ok) {
        clearAccessToken();
        return null;
      }

      const data = await response.json();
      const token: string | undefined = data?.access_token;
      if (!token) {
        clearAccessToken();
        return null;
      }

      setAccessToken(token);
      scheduleRefresh(token);
      return token;
    } catch {
      // Network failure: keep the current token and let the scheduled retry
      // handle it, rather than signing the user out over a blip.
      return null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/** Queue the next renewal based on the token's own expiry. */
export function scheduleRefresh(token: string | null = getAccessToken()): void {
  stopRefresh();
  if (!token) return;

  const remaining = millisUntilExpiry(token);
  const delay =
    remaining === null
      ? DEFAULT_REFRESH_MS
      : Math.max(5_000, remaining - REFRESH_MARGIN_MS);

  refreshTimer = setTimeout(() => {
    void refreshAccessToken();
  }, delay);
}

export function stopRefresh(): void {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

/**
 * Start renewing on app load or immediately after signing in.
 *
 * Also refreshes when the tab regains focus: background tabs get their timers
 * throttled by the browser, so a tab left open can wake with a token that
 * expired while it slept.
 */
export function startSession(token?: string): void {
  if (token) setAccessToken(token);
  scheduleRefresh(token ?? getAccessToken());

  if (!visibilityBound) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      const current = getAccessToken();
      if (!current) return;
      const remaining = millisUntilExpiry(current);
      if (remaining !== null && remaining < REFRESH_MARGIN_MS) {
        void refreshAccessToken();
      } else {
        scheduleRefresh(current);
      }
    });
    visibilityBound = true;
  }
}

let visibilityBound = false;

export function endSession(): void {
  stopRefresh();
  clearAccessToken();
}
